<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { tokenize, tokensToHtml, getFieldSuggestions, getAllFields } from '@/utils/queryTokenizer'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  multiline: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  showAutocomplete: {
    type: Boolean,
    default: true
  },
})

const emit = defineEmits(['update:modelValue', 'submit'])

// Sanitized value for display (strips newlines in single-line mode)
const displayValue = computed(() => {
  if (!props.modelValue) return ''
  return props.multiline ? props.modelValue : props.modelValue.replace(/[\r\n]+/g, ' ')
})

// Local value for v-model
const localValue = computed({
  get: () => displayValue.value,
  set: (value) => {
    // Always preserve original value including newlines
    // Display sanitization is handled by displayValue computed
    emit('update:modelValue', value)
  }
})

// Refs
const containerRef = ref(null)
const inputRef = ref(null)
const highlightRef = ref(null)
const dropdownRef = ref(null)
const measuringRef = ref(null)
const measuringSpan = ref(null)
const cursorMarker = ref(null)

// Autocomplete state
const cursorPosition = ref(0)
const currentWord = ref({ word: '', startPos: 0, endPos: 0 })
const matchedFields = ref([])
const selectedIndex = ref(0)
const showDropdown = ref(false)
const shadowSuggestion = ref('')
const dropdownStyle = ref({})

// Generate highlighted HTML from query
const highlightedHtml = computed(() => {
  if (!displayValue.value) return ''
  const tokens = tokenize(displayValue.value)
  return tokensToHtml(tokens)
})

// Dynamic placeholder - hide when dropdown is showing
const dynamicPlaceholder = computed(() => {
  return showDropdown.value ? '' : props.placeholder
})

// Get all fields for autocomplete
const allFields = computed(() => getAllFields())

// Query parser utilities
function getCurrentWord(text, cursorPos) {
  const wordBoundaries = /[\s(),"]/

  let startPos = cursorPos
  let endPos = cursorPos

  // Find start of word
  while (startPos > 0 && !wordBoundaries.test(text[startPos - 1])) {
    startPos--
  }

  // Find end of word
  while (endPos < text.length && !wordBoundaries.test(text[endPos])) {
    endPos++
  }

  const word = text.substring(startPos, endPos)
  return { word, startPos, endPos }
}

function isValueContext(text, wordStartPos) {
  if (wordStartPos === 0) return false

  let pos = wordStartPos - 1

  // Skip any whitespace before the current word
  while (pos >= 0 && text[pos] === ' ') {
    pos--
  }

  if (pos < 0) return false

  // Check if we're after a colon (in value context)
  return text[pos] === ':'
}

function matchFields(partial) {
  if (!partial || partial.length < 1) return []
  return getFieldSuggestions(partial)
}

// Shadow suggestion for tab completion
function updateShadowSuggestion() {
  if (matchedFields.value.length === 0) {
    shadowSuggestion.value = ''
    return
  }

  const selectedField = matchedFields.value[selectedIndex.value]
  const currentPartial = currentWord.value.word.toLowerCase()
  const fieldName = selectedField.name.toLowerCase()

  if (fieldName.startsWith(currentPartial)) {
    shadowSuggestion.value = selectedField.name.substring(currentWord.value.word.length)
  } else {
    shadowSuggestion.value = ''
  }
}

// Cursor position calculation for dropdown placement
function getCursorCoordinates() {
  if (!inputRef.value || !measuringRef.value || !measuringSpan.value || !cursorMarker.value) {
    return { left: 0, top: 0 }
  }

  // Use actual input value to ensure sync
  const inputValue = inputRef.value.value || props.modelValue
  const textBeforeCursor = inputValue.substring(0, cursorPosition.value)
  measuringSpan.value.textContent = textBeforeCursor

  const markerRect = cursorMarker.value.getBoundingClientRect()
  const measuringRect = measuringRef.value.getBoundingClientRect()

  const left = markerRect.left - measuringRect.left
  const top = markerRect.top - measuringRect.top

  return { left, top }
}

function updateDropdownPosition() {
  if (!inputRef.value || !showDropdown.value) return

  const inputRect = inputRef.value.getBoundingClientRect()
  const cursorCoords = getCursorCoordinates()

  const lineHeight = props.multiline ? 24 : 20

  let top = inputRect.top + cursorCoords.top + lineHeight
  let left = inputRect.left + cursorCoords.left

  // Check if dropdown would go off bottom of viewport
  const dropdownHeight = 300
  const viewportHeight = window.innerHeight

  if (top + dropdownHeight > viewportHeight) {
    top = inputRect.top + cursorCoords.top - dropdownHeight - 4
  }

  // Ensure left doesn't go off screen
  const minWidth = 400
  if (left + minWidth > window.innerWidth) {
    left = window.innerWidth - minWidth - 16
  }

  dropdownStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    minWidth: '400px',
    maxWidth: '600px'
  }
}

async function scrollDropdownToSelected() {
  if (!dropdownRef.value) return

  await nextTick()

  const selectedElement = dropdownRef.value.querySelector('.dropdown-item.selected')
  if (selectedElement) {
    selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }
}

// Scroll synchronization
function syncScroll() {
  if (!inputRef.value || !highlightRef.value) return
  highlightRef.value.scrollTop = inputRef.value.scrollTop
  highlightRef.value.scrollLeft = inputRef.value.scrollLeft
}

// Input handlers
function handleInput(event) {
  if (!props.showAutocomplete) return

  // Use event.target.value since v-model update may not have propagated yet
  const inputValue = event.target.value
  cursorPosition.value = event.target.selectionStart
  currentWord.value = getCurrentWord(inputValue, cursorPosition.value)

  const isValue = isValueContext(inputValue, currentWord.value.startPos)

  if (isValue) {
    closeAutocomplete()
    return
  }

  // Match fields if we have at least 2 characters
  if (currentWord.value.word.length >= 2) {
    matchedFields.value = matchFields(currentWord.value.word)
    selectedIndex.value = 0

    if (matchedFields.value.length > 1) {
      showDropdown.value = true
      updateDropdownPosition()
    } else if (matchedFields.value.length === 1) {
      showDropdown.value = false
    } else {
      closeAutocomplete()
      return
    }

    updateShadowSuggestion()
  } else {
    closeAutocomplete()
  }
}

function updateCursorPosition(event) {
  cursorPosition.value = event.target.selectionStart
  // Also update current word to keep it in sync
  currentWord.value = getCurrentWord(event.target.value, cursorPosition.value)
}

function handleKeydown(event) {
  // Ctrl+Space: Show all fields
  if ((event.ctrlKey || event.metaKey) && event.code === 'Space') {
    if (!props.showAutocomplete) return

    event.preventDefault()

    // Update current word from the actual input value
    const inputValue = inputRef.value?.value || props.modelValue
    cursorPosition.value = inputRef.value?.selectionStart || 0
    currentWord.value = getCurrentWord(inputValue, cursorPosition.value)

    if (currentWord.value.word.length >= 1) {
      matchedFields.value = matchFields(currentWord.value.word)
    } else {
      matchedFields.value = allFields.value.slice(0, 50)
    }

    showDropdown.value = true
    selectedIndex.value = 0
    updateDropdownPosition()
    updateShadowSuggestion()
    return
  }

  // Tab / Shift+Tab: Complete single match OR cycle dropdown
  if (event.key === 'Tab' && props.showAutocomplete) {
    if (showDropdown.value && matchedFields.value.length > 0) {
      event.preventDefault()
      if (event.shiftKey) {
        selectedIndex.value = selectedIndex.value <= 0
          ? matchedFields.value.length - 1
          : selectedIndex.value - 1
      } else {
        selectedIndex.value = (selectedIndex.value + 1) % matchedFields.value.length
      }
      updateShadowSuggestion()
      scrollDropdownToSelected()
    } else if (!showDropdown.value && shadowSuggestion.value && matchedFields.value.length === 1) {
      event.preventDefault()
      completeField(matchedFields.value[0].name)
    }
    return
  }

  // Enter: Accept dropdown selection OR submit
  if (event.key === 'Enter') {
    if (showDropdown.value && matchedFields.value.length > 0) {
      event.preventDefault()
      completeField(matchedFields.value[selectedIndex.value].name)
      return
    }

    // For single-line, Enter submits
    // For multi-line, Ctrl/Cmd+Enter submits
    if (!props.multiline || event.ctrlKey || event.metaKey) {
      event.preventDefault()
      emit('submit')
      return
    }
  }

  // Arrow Down: Navigate dropdown
  if (event.key === 'ArrowDown' && showDropdown.value) {
    event.preventDefault()
    selectedIndex.value = (selectedIndex.value + 1) % matchedFields.value.length
    updateShadowSuggestion()
    scrollDropdownToSelected()
    return
  }

  // Arrow Up: Navigate dropdown
  if (event.key === 'ArrowUp' && showDropdown.value) {
    event.preventDefault()
    selectedIndex.value = selectedIndex.value <= 0
      ? matchedFields.value.length - 1
      : selectedIndex.value - 1
    updateShadowSuggestion()
    scrollDropdownToSelected()
    return
  }

  // Escape: Close autocomplete
  if (event.key === 'Escape') {
    event.preventDefault()
    closeAutocomplete()
    return
  }
}

function completeField(fieldName) {
  const before = props.modelValue.substring(0, currentWord.value.startPos)
  const after = props.modelValue.substring(currentWord.value.endPos)
  localValue.value = before + fieldName + after

  nextTick(() => {
    if (inputRef.value) {
      const newCursorPos = currentWord.value.startPos + fieldName.length
      inputRef.value.selectionStart = newCursorPos
      inputRef.value.selectionEnd = newCursorPos
      cursorPosition.value = newCursorPos
      inputRef.value.focus()
    }
  })

  closeAutocomplete()
}

function closeAutocomplete() {
  showDropdown.value = false
  shadowSuggestion.value = ''
  matchedFields.value = []
  selectedIndex.value = 0
}

// Click-outside handler
function handleClickOutside(event) {
  if (containerRef.value && !containerRef.value.contains(event.target)) {
    closeAutocomplete()
  }
}

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('scroll', updateDropdownPosition, true)
  window.addEventListener('resize', updateDropdownPosition)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('scroll', updateDropdownPosition, true)
  window.removeEventListener('resize', updateDropdownPosition)
})

// Public method to focus the input
function focus() {
  inputRef.value?.focus()
}

defineExpose({ focus })
</script>

<template>
  <div ref="containerRef" class="syntax-input-container" :class="{ multiline }">
    <!-- Highlight layer (behind input) -->
    <div
      ref="highlightRef"
      class="highlight-layer"
      :class="{ multiline }"
    >
      <span v-html="highlightedHtml"></span><span class="suggestion-text">{{ shadowSuggestion }}</span>
    </div>

    <!-- Measuring element for cursor position -->
    <div ref="measuringRef" class="measuring-layer" :class="{ multiline }">
      <span ref="measuringSpan"></span><span ref="cursorMarker" class="cursor-marker"></span>
    </div>

    <!-- Actual input (foreground) -->
    <textarea
      v-if="multiline"
      ref="inputRef"
      v-model="localValue"
      :placeholder="dynamicPlaceholder"
      :disabled="disabled"
      class="input-layer multiline"
      @input="handleInput"
      @keydown="handleKeydown"
      @click="updateCursorPosition"
      @keyup="updateCursorPosition"
      @scroll="syncScroll"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="showDropdown"
      aria-controls="autocomplete-dropdown"
    ></textarea>
    <input
      v-else
      ref="inputRef"
      type="text"
      v-model="localValue"
      :placeholder="dynamicPlaceholder"
      :disabled="disabled"
      class="input-layer"
      @input="handleInput"
      @keydown="handleKeydown"
      @click="updateCursorPosition"
      @keyup="updateCursorPosition"
      @scroll="syncScroll"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="showDropdown"
      aria-controls="autocomplete-dropdown"
    />

    <!-- Autocomplete dropdown -->
    <Transition name="dropdown">
      <div
        v-if="showDropdown && matchedFields.length > 0"
        id="autocomplete-dropdown"
        ref="dropdownRef"
        class="autocomplete-dropdown"
        :style="dropdownStyle"
        role="listbox"
      >
        <div
          v-for="(field, index) in matchedFields"
          :key="field.name"
          class="dropdown-item"
          :class="{ selected: index === selectedIndex }"
          role="option"
          :aria-selected="index === selectedIndex"
          @mouseenter="selectedIndex = index; updateShadowSuggestion()"
          @click="completeField(field.name)"
        >
          <code class="field-name">{{ field.name }}</code>
          <p class="field-description">{{ field.description }}</p>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.syntax-input-container {
  position: relative;
  width: 100%;
}

.syntax-input-container:not(.multiline) {
  height: 3.5rem;
}

.syntax-input-container.multiline {
  height: 100%;
}

/* Highlight layer */
.highlight-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: 1rem 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 1rem;
  line-height: 1.5;
  white-space: pre;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  pointer-events: none;
  box-sizing: border-box;
}

.highlight-layer::-webkit-scrollbar {
  display: none;
}

.highlight-layer.multiline {
  height: 100%;
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 1rem;
  overflow-y: auto;
}

.suggestion-text {
  color: rgba(255, 255, 255, 0.25);
}

/* Measuring layer */
.measuring-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: 1rem 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 1rem;
  line-height: 1.5;
  white-space: pre;
  visibility: hidden;
  pointer-events: none;
  z-index: -1;
  box-sizing: border-box;
}

.measuring-layer.multiline {
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 1rem;
}

.cursor-marker {
  display: inline;
  width: 0;
  height: 0;
}

/* Input layer */
.input-layer {
  position: relative;
  width: 100%;
  padding: 1rem 0;
  background: transparent;
  border: none;
  outline: none;
  color: transparent;
  caret-color: white;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 1rem;
  line-height: 1.5;
  z-index: 1;
  box-sizing: border-box;
}

.input-layer.multiline {
  height: 100%;
  resize: none;
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 1rem;
  overflow-y: auto;
}

.input-layer::placeholder {
  color: #6B7280;
}

.input-layer:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Token colors */
:deep(.token-field) {
  color: #F59E0B; /* Orange - valid field */
}

:deep(.token-invalid-field) {
  color: #EF4444; /* Red - invalid field */
}

:deep(.token-operator) {
  color: #8B5CF6; /* Purple - operators */
}

:deep(.token-quoted) {
  color: #10B981; /* Green - quoted strings */
}

:deep(.token-structural) {
  color: #6B7280; /* Gray - structural chars (default) */
}

:deep(.token-text) {
  color: #FFFFFF; /* White - plain text/values */
}

/* Autocomplete dropdown */
.autocomplete-dropdown {
  position: fixed;
  background-color: #1B2029;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  max-height: 300px;
  overflow-y: auto;
  z-index: 10000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.autocomplete-dropdown::-webkit-scrollbar {
  width: 6px;
}

.autocomplete-dropdown::-webkit-scrollbar-track {
  background: transparent;
}

.autocomplete-dropdown::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.autocomplete-dropdown::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.15);
}

.dropdown-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover,
.dropdown-item.selected {
  background-color: rgba(255, 255, 255, 0.05);
}

.field-name {
  color: #E5E7EB;
  font-size: 0.875rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-weight: 500;
  display: block;
  margin-right: 1rem;
}

.field-description {
  color: #6B7280;
  font-size: 0.75rem;
  margin: 0;
  line-height: 1.3;
}

/* Transition animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
